import type { CatalogueItemsController_create_v1Body, OutletResource } from '@repo/api-client';

import { parseCsv } from '@/lib/csv';
import { catalogueCodeSchema, catalogueNameSchema } from '@/lib/field-schemas';
import { isRepresentablePaise } from '@/lib/money';

import {
  CATALOGUE_KINDS,
  type CatalogueKind,
  labelForKind,
  mayTrackStock,
} from './catalogue-kinds';

/**
 * A catalogue file, read and judged — every row of it, before the first request.
 *
 * ## Why the review step is not decoration
 *
 * `code` and `kind` are **immutable once set**. There is no route that changes
 * either: `UpdateCatalogueItemDto` carries `name` and `tracksStock` and nothing
 * else. A typo in a code is therefore permanent — the item can be retired, but
 * the code is never reissued (FR-215), so it cannot be corrected and cannot be
 * used again. Five hundred rows of that arriving on one press is precisely what
 * a review step exists to stand in front of.
 *
 * So everything the API can refuse a row for is checked here first, against what
 * this caller can actually see, and a rejected row never becomes a request:
 *
 * | The API's refusal | Caught here as |
 * |---|---|
 * | `CATALOGUE_STOCK_ON_NON_PRODUCT` (422) | stock tracking asked for on a ticket type or an activity |
 * | `CATALOGUE_STOCK_NOT_AVAILABLE_HERE` (409) | a stock-tracked product naming an outlet that carries no stock |
 * | `CATALOGUE_PRICE_INVALID` (422) | an amount that is not whole, non-negative paise |
 * | `RESOURCE_NOT_FOUND` (404) | an outlet name or code that is not one of the caller's |
 *
 * **`CATALOGUE_CODE_TAKEN` is deliberately not on that list.** This app cannot
 * know which codes the park has already issued without reading the entire
 * catalogue, live and retired, and it does not need to: a collision means that
 * row is already created, which is a skip rather than a failure. That decision
 * lives in `catalogue-upload-run.ts` and is what makes re-running the same file
 * safe. What *is* caught here is a code the **file itself** uses twice, which is
 * a different thing — the first row would create and the second would come back
 * as "already existed", reading as a clean re-run when it is a mistake in the
 * file.
 *
 * ## A row is named by where it is and what it is called
 *
 * `rowNumber` counts the file's own lines with the header as line 1, so it is
 * the number a spreadsheet shows in its gutter, and `name` is the text that was
 * typed even when nothing else about the row is usable. **Never an id**: at the
 * point a row is rejected there is no id, and a UUID is no handle on a
 * spreadsheet somebody is holding.
 *
 * ## Money in the file is paise
 *
 * Whole paise, as the API carries it and as the item export writes it: twenty
 * rupees is `2000`, never `20.00` and never `20`. A value written as rupees is
 * refused with a message that says so rather than being multiplied by a hundred
 * on a guess — a file mixing the two conventions would otherwise create half its
 * items at a hundredth of their price, correctable only by repricing every one.
 */

/** The columns a file may carry, in the order the template writes them. */
export const CATALOGUE_UPLOAD_COLUMNS = [
  'kind',
  'code',
  'name',
  'tracksStock',
  'sellingPaise',
  'costPaise',
  'outlets',
] as const;

/**
 * The columns without which a row cannot be built at all.
 *
 * `tracksStock` and `outlets` are absent because both mean something when they
 * are blank — off, and nowhere yet — and both are legitimate for a whole file.
 * The other five have no default this app is entitled to invent: a missing price
 * is not zero, it is a price nobody stated.
 */
export const REQUIRED_UPLOAD_COLUMNS = [
  'kind',
  'code',
  'name',
  'sellingPaise',
  'costPaise',
] as const;

/** How several outlets are written in one cell. */
export const OUTLET_SEPARATOR = ';';

/** What every row carries, whether or not it can be created. */
interface RowIdentity {
  /** The file's own line, header included, so it is the number in the gutter. */
  readonly rowNumber: number;

  /** The name as typed. What a person holding the file recognises. */
  readonly name: string;

  /** The code as typed, uppercased. Empty when the row carried none. */
  readonly code: string;
}

/** A row that will be sent, and the body it will be sent as. */
export interface ReadyRow extends RowIdentity {
  readonly ready: true;

  /** Built from the generated operation's own body type. Nothing hand-shaped. */
  readonly body: CatalogueItemsController_create_v1Body;

  /** The outlets named, for the review table. The ids are in the body. */
  readonly outletNames: readonly string[];
}

/** A row that will not be sent, and every reason why. */
export interface RejectedRow extends RowIdentity {
  readonly ready: false;

  /**
   * All of them, not the first.
   *
   * A row with a bad kind and a bad price is two corrections, and reporting one
   * at a time is a person fixing the file, uploading it again, and being told
   * about the next one — five hundred rows at a time.
   */
  readonly problems: readonly string[];
}

export type UploadRow = ReadyRow | RejectedRow;

/**
 * A file that was read, or one that was not a catalogue file at all.
 *
 * The second is a different answer from "every row is bad": nothing about it can
 * be corrected row by row, so the screen says what a file must carry rather than
 * listing five hundred identical problems.
 */
export type CatalogueUploadFile =
  | { readonly kind: 'read'; readonly rows: readonly UploadRow[] }
  | { readonly kind: 'unreadable'; readonly problem: string };

/** What the review step counts up before anybody presses the button. */
export interface UploadSummary {
  readonly ready: number;
  readonly rejected: number;

  /**
   * Rows that will be created with a cost of zero.
   *
   * Counted and shown, because **a zero cost is not a neutral default**: it
   * flows straight into gross margin on the profit and loss and on item
   * reporting, where it reads as 100% margin. Zero is legitimate — the API
   * accepts it deliberately, and a souvenir nobody tracks a cost for is a real
   * case — so this is surfaced as a decision to take knowingly, never refused.
   */
  readonly zeroCost: number;

  /** Rows that will count stock. Only ever products (FR-212). */
  readonly tracksStock: number;
}

/**
 * The bytes of a catalogue file, read against the outlets this caller can see.
 *
 * The outlets are passed in rather than fetched here, for the reason
 * `NewCatalogueItemDialog` passes them into its form: the screen has already
 * read them, and the `carriesStock` this reasons about must be the same copy the
 * review table is showing. A second read is a second answer.
 */
export function readCatalogueUpload(
  text: string,
  outlets: readonly OutletResource[],
): CatalogueUploadFile {
  const [header, ...body] = parseCsv(text);

  if (header === undefined) {
    return {
      kind: 'unreadable',
      problem: `That file is empty. A catalogue file starts with a header row naming its columns: ${CATALOGUE_UPLOAD_COLUMNS.join(', ')}.`,
    };
  }

  // Matched loosely — trimmed, case-folded, separators levelled — because the
  // header is typed by a person or written by a spreadsheet, and refusing `Code`
  // for not being `code` is a refusal nobody can act on. Unknown columns are
  // ignored rather than refused: a file carrying a notes column somebody keeps
  // for themselves is still a file this can read.
  const columnAt = new Map(header.map((column, at) => [normalise(column), at]));

  const missing = REQUIRED_UPLOAD_COLUMNS.filter((column) => !columnAt.has(normalise(column)));

  if (missing.length > 0) {
    return {
      kind: 'unreadable',
      problem: `That file is missing ${missing.length === 1 ? 'a column' : 'columns'}: ${missing.join(', ')}. A catalogue file carries ${CATALOGUE_UPLOAD_COLUMNS.join(', ')}, and ${REQUIRED_UPLOAD_COLUMNS.join(', ')} must each be there.`,
    };
  }

  // A row with nothing in it at all is dropped rather than rejected. A
  // spreadsheet leaves them behind constantly, and five hundred "this row is
  // empty" problems bury the two that matter.
  const rows = body.filter((cells) => cells.some((cell) => cell.trim() !== ''));

  if (rows.length === 0) {
    return {
      kind: 'unreadable',
      problem: 'That file has a header and no items under it. Add a row for each item to create.',
    };
  }

  // Codes already claimed by an earlier row of this same file, per kind, so it
  // is the *second* use that is rejected and it can say where the first was.
  const claimed = new Map<string, number>();

  return {
    kind: 'read',
    rows: rows.map((cells, at) =>
      readRow({
        // The header is line 1 and this list starts at the line after it.
        rowNumber: at + 2,
        cell: (column) => cells[columnAt.get(normalise(column)) ?? -1]?.trim() ?? '',
        outlets,
        claimed,
      }),
    ),
  };
}

/** What a review step says about a file, counted from its own rows. */
export function summariseUpload(rows: readonly UploadRow[]): UploadSummary {
  const ready = rows.filter(isReady);

  return {
    ready: ready.length,
    rejected: rows.length - ready.length,

    // Counted over the ready rows alone. A rejected row is not being created, so
    // its cost is not a margin anybody will ever read.
    zeroCost: ready.filter((row) => row.body.costPaise === 0).length,
    tracksStock: ready.filter((row) => row.body.tracksStock).length,
  };
}

/** Narrowing helper, so callers do not each write the discriminant by hand. */
export function isReady(row: UploadRow): row is ReadyRow {
  return row.ready;
}

/* -------------------------------------------------------------------------- */
/* One row                                                                     */
/* -------------------------------------------------------------------------- */

function readRow({
  rowNumber,
  cell,
  outlets,
  claimed,
}: {
  rowNumber: number;
  cell: (column: string) => string;
  outlets: readonly OutletResource[];
  claimed: Map<string, number>;
}): UploadRow {
  const problems: string[] = [];

  const name = cell('name');
  const typedCode = cell('code');
  const code = typedCode.toUpperCase();

  const identity = { rowNumber, name, code };

  const typedKind = cell('kind');
  const kind = readKind(typedKind);

  if (kind === null) {
    problems.push(
      `${typedKind === '' ? 'No kind was given' : `${quoted(typedKind)} is not a kind`}. It is one of ${CATALOGUE_KINDS.join(', ')}.`,
    );
  }

  const nameCheck = catalogueNameSchema.safeParse(name);

  if (!nameCheck.success) problems.push(firstMessage(nameCheck.error));

  const codeCheck = catalogueCodeSchema.safeParse(typedCode);

  if (!codeCheck.success) problems.push(firstMessage(codeCheck.error));

  // Only once the code and the kind are both good: uniqueness is per kind, so
  // there is nothing to claim until both are known.
  if (codeCheck.success && kind !== null) {
    const key = `${kind} ${code}`;
    const earlier = claimed.get(key);

    if (earlier === undefined) {
      claimed.set(key, rowNumber);
    } else {
      problems.push(
        `This file already uses the code ${code} for a ${labelForKind(kind).toLowerCase()} on row ${String(earlier)}. A code is issued once for all time within its kind and is never reissued, so only one of these rows can have it.`,
      );
    }
  }

  const selling = readPaise(cell('sellingPaise'), 'selling price');
  const cost = readPaise(cell('costPaise'), 'cost price');

  if (typeof selling === 'string') problems.push(selling);
  if (typeof cost === 'string') problems.push(cost);

  const typedFlag = cell('tracksStock');
  const tracksStock = readFlag(typedFlag);

  if (tracksStock === null) {
    problems.push(
      `${quoted(typedFlag)} is not a yes or a no for tracksStock. Write yes or no, or leave it blank for no.`,
    );
  }

  // FR-212, and the same rule `NewCatalogueItemDialog` disables its switch for.
  // Caught here rather than sent: the API answers this with
  // `CATALOGUE_STOCK_ON_NON_PRODUCT`, and a refusal per row is a refusal a
  // person reads five hundred times.
  if (tracksStock === true && kind !== null && !mayTrackStock(kind)) {
    problems.push(
      `Only a product can count stock, so tracksStock cannot be yes for a ${labelForKind(kind).toLowerCase()}.`,
    );
  }

  const named = readOutlets(cell('outlets'), outlets);

  problems.push(...named.problems);

  // FR-208. The whole create is refused when this is wrong and nothing about the
  // row is written, which is what makes catching it here worth more than the one
  // request it saves.
  if (tracksStock === true && named.chosen.length > 0) {
    const without = named.chosen.filter((outlet) => !outlet.carriesStock);

    if (without.length > 0) {
      const one = without.length === 1;

      problems.push(
        `${listOf(without.map((outlet) => outlet.name))} ${one ? 'does' : 'do'} not carry stock, so ${one ? 'it' : 'they'} cannot sell an item that counts stock. Clear ${one ? 'that outlet' : 'those outlets'}, or set tracksStock to no.`,
      );
    }
  }

  if (
    problems.length > 0 ||
    kind === null ||
    tracksStock === null ||
    typeof selling !== 'number' ||
    typeof cost !== 'number'
  ) {
    return { ...identity, ready: false, problems };
  }

  return {
    ...identity,
    ready: true,
    outletNames: named.chosen.map((outlet) => outlet.name),
    body: {
      kind,
      code,
      name,
      tracksStock,
      sellingPaise: selling,
      costPaise: cost,

      // Omitted rather than sent as `[]`, exactly as the create form does. The
      // two mean the same thing to the API — available nowhere — and the request
      // in a log should say what was asked for rather than what was defaulted.
      ...(named.chosen.length > 0 ? { outletIds: named.chosen.map((outlet) => outlet.id) } : {}),
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Cells                                                                       */
/* -------------------------------------------------------------------------- */

/** Trimmed, folded, separators levelled — for matching a name against a name. */
function normalise(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');
}

/**
 * A kind, or `null`.
 *
 * Read off `CATALOGUE_KINDS` rather than against a list written here, so a kind
 * added to the document is a kind this accepts without anybody remembering to
 * come back. **The document declares three** — `PRODUCT`, `TICKET_TYPE` and
 * `ACTIVITY` — where the ticket names two; the third is not this screen's
 * invention, and refusing it would refuse a value the API creates happily.
 *
 * `ticket type`, `Ticket Type` and `TICKET_TYPE` are one answer, because this is
 * typed into a spreadsheet by a person and a space is what they type.
 */
function readKind(cell: string): CatalogueKind | null {
  return CATALOGUE_KINDS.find((kind) => normalise(kind) === normalise(cell)) ?? null;
}

/**
 * Whole, non-negative paise, or the sentence saying why not.
 *
 * The digits are matched before any conversion. `Number('')` is `0` and
 * `Number(' ')` is `0`, so a blank price would otherwise read as free — a real
 * and completely different answer, and one nothing but a reprice can undo.
 *
 * A value written as rupees gets its own sentence. It is the single most likely
 * mistake in a file a person filled in, and accepted quietly it creates an item
 * at a hundredth of its price.
 */
function readPaise(cell: string, what: string): number | string {
  if (cell === '') {
    return `No ${what} was given. Amounts in this file are whole paise, so twenty rupees is written 2000.`;
  }

  if (/^\d+\.\d+$/.test(cell)) {
    const [rupees = '', fraction = ''] = cell.split('.');

    return `The ${what} ${quoted(cell)} looks like rupees. Amounts in this file are whole paise, so it is written ${rupees}${fraction.padEnd(2, '0').slice(0, 2)}.`;
  }

  if (!/^\d+$/.test(cell)) {
    return `The ${what} ${quoted(cell)} is not an amount. Amounts are whole paise, zero or more, with no symbol and no separators.`;
  }

  const paise = Number(cell);

  // Not a business rule, and deliberately not worded as one: past
  // `MAX_SAFE_INTEGER` the value stops being the value the file carried before
  // the request is even built, so sending it would be sending a number nobody
  // wrote. The API's own bound is its own, and it answers
  // `CATALOGUE_PRICE_INVALID`.
  if (!isRepresentablePaise(paise)) {
    return `The ${what} ${quoted(cell)} is larger than an amount this system can carry.`;
  }

  return paise;
}

/** A yes or a no, blank being no. `null` for anything else. */
function readFlag(cell: string): boolean | null {
  if (cell === '') return false;

  const value = cell.toLowerCase();

  if (['yes', 'y', 'true', '1', 'on'].includes(value)) return true;
  if (['no', 'n', 'false', '0', 'off'].includes(value)) return false;

  return null;
}

/**
 * The outlets a cell names, matched against the ones this caller can see.
 *
 * By **code first, then name**, both case-insensitively. A code is the
 * identifier and is what the exports write; a name is what somebody types from
 * memory, and matching it is the difference between a file a person can fill in
 * and one they cannot. The order matters where a name and a code collide, and it
 * is settled here rather than left to whichever the list happens to hold first.
 *
 * An unmatched value is named back exactly as it was typed, which is what makes
 * the problem actionable. This is the `RESOURCE_NOT_FOUND` the API would answer
 * with, caught before it costs a request — and before it costs the rest of the
 * row, since that refusal writes nothing at all.
 */
function readOutlets(
  cell: string,
  outlets: readonly OutletResource[],
): { chosen: readonly OutletResource[]; problems: readonly string[] } {
  const typed = cell
    .split(OUTLET_SEPARATOR)
    .map((part) => part.trim())
    .filter((part) => part !== '');

  const chosen: OutletResource[] = [];
  const problems: string[] = [];

  for (const value of typed) {
    const byCode = outlets.filter((outlet) => normalise(outlet.code) === normalise(value));

    const matches =
      byCode.length > 0
        ? byCode
        : outlets.filter((outlet) => normalise(outlet.name) === normalise(value));

    const [match] = matches;

    if (match === undefined) {
      problems.push(
        `There is no outlet called ${quoted(value)} that you can reach. Use the outlet's code or its name as it appears on the Outlets screen, and separate several with a ${OUTLET_SEPARATOR}.`,
      );
    } else if (matches.length > 1) {
      problems.push(
        `${quoted(value)} is the name of more than one outlet, so this row does not say which. Use the outlet's code instead.`,
      );
    } else if (!chosen.includes(match)) {
      // Named twice in one cell is not worth a problem — the set is a set — but
      // it must not be sent twice.
      chosen.push(match);
    }
  }

  return { chosen, problems };
}

/** The resolver's first message for a field, which is the one worth showing. */
function firstMessage(error: { issues: readonly { message: string }[] }): string {
  return error.issues[0]?.message ?? 'That value cannot be used.';
}

/** A person's own text, quoted, so it is clear where it starts and stops. */
function quoted(value: string): string {
  return `“${value}”`;
}

/** "A, B and C", in the app's locale. */
function listOf(names: readonly string[]): string {
  return new Intl.ListFormat('en-IN', { type: 'conjunction' }).format(names);
}
